import { useEffect, useState } from 'react';
import Icon, { IconType } from '../Icon/Icon';

const SHOW_AFTER_PX = 300;

// overflow-x: hidden on html/body computes overflow-y to auto, so long pages
// scroll on body while documentElement stays viewport-sized.
const scrollRoots = (): HTMLElement[] => {
  const roots: HTMLElement[] = [];
  const add = (el: Element | null) => {
    if (el instanceof HTMLElement && !roots.includes(el)) roots.push(el);
  };
  add(document.scrollingElement);
  add(document.documentElement);
  add(document.body);
  return roots;
};

const pageIsLong = () =>
  scrollRoots().some((el) => el.scrollHeight > el.clientHeight + SHOW_AFTER_PX);

const scrolledDown = () => {
  const top = Math.max(window.pageYOffset || 0, ...scrollRoots().map((el) => el.scrollTop || 0));
  return top > SHOW_AFTER_PX;
};

const scrollPageToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
  scrollRoots().forEach((el) => el.scrollTo({ top: 0, behavior: 'smooth' }));
};

const ScrollToTopButton = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const update = () => setVisible(pageIsLong() && scrolledDown());
    update();
    document.addEventListener('scroll', update, { passive: true, capture: true });
    window.addEventListener('resize', update);
    const observer = new ResizeObserver(update);
    scrollRoots().forEach((el) => observer.observe(el));
    return () => {
      document.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      observer.disconnect();
    };
  }, []);

  if (!visible) return null;

  return (
    <button
      type="button"
      className="scroll-to-top-btn"
      aria-label="Scroll to top"
      onClick={scrollPageToTop}
    >
      <Icon type={IconType.expandUp} />
    </button>
  );
};

export default ScrollToTopButton;
